import type { ContributionEntry, InvestmentContributionResponse } from "@eu.mdotm.api/sphere";
import { Card } from "$root/components/EvolvedPrint/components/Card";
import {
	TinyTableDataCell,
	TinyTableHeadCell,
	tinyTableHeadCellFontSize,
} from "$root/components/EvolvedPrint/components/table/tiny-table";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import { formatNumber } from "$root/localization/formatters";
import { BarGraphPCSvg } from "$root/ui-lib/charts";
import {
	DrillDownByValueMap,
	getGraphMarkers2,
	objSumRounded,
	roundCustomByStep,
	typedObjectValues,
} from "$root/utils";
import {
	type BaseTableProps,
	type TableWithGroupsProps,
	BaseTable,
	BaseTableWithGroups,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { groupBy } from "@mdotm/utils/collections";
import { ForEach } from "@mdotm/utils/react-extra";
import Immutable from "immutable";
import { useCallback, useMemo } from "react";
import { useReportTranslation } from "../translation";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";

export type PerformanceContributionVolatilitySelectionProps =
	| { kind: "grouped"; selector: "assetClass"; vsSelector: "microAssetClass" }
	| { kind: "grouped"; selector: "assetClass"; vsSelector: "geography" }
	| { kind: "grouped"; selector: "microAssetClass"; vsSelector: "geography" }
	| { kind: "grouped"; selector: "geography"; vsSelector: "assetClass" }
	| { kind: "grouped"; selector: "geography"; vsSelector: "microAssetClass" }
	| { kind: "composition" };

const groupedOptions = [
	{
		label: "Macro Asset Class vs Micro Asset Class",
		selector: "assetClass",
		vsSelector: "microAssetClass",
		key: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
	},
	{
		label: "Macro Asset Class vs Geography",
		selector: "assetClass",
		vsSelector: "geography",
		key: "MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	},
	{
		label: "Micro Asset Class vs Geography",
		selector: "microAssetClass",
		vsSelector: "geography",
		key: "MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	},
	{
		label: "Geography vs Macro Asset Class",
		selector: "geography",
		vsSelector: "assetClass",
		key: "MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS",
	},
	{
		label: "Geography vs Micro Asset Class",
		selector: "geography",
		vsSelector: "microAssetClass",
		key: "MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS",
	},
] as const;

type DrillDownProps = {
	index: number;
	value: string | number | ContributionEntry | undefined;
	computedData: Record<
		"volatilityValue" | "performanceValue" | "maxVol" | "minVol" | "maxPer" | "minPer",
		number | null
	>;
};

type FlatInstrumentRow = {
	index: number;
	value: string;
	computedData: { performanceValue: number | null; volatilityValue: number | null };
};

export type PerformanceVolatilityContributionListItem =
	| { kind: "grouped"; group: DrillDownProps; drilldown: DrillDownProps }
	| { kind: "composition"; row: FlatInstrumentRow };

type GetPerformanceVolatilityContributionProps = {
	graphColumnMarkers: {
		markersVolatility: {
			value: number;
			label: string;
		}[];
		markersPerformance: {
			value: number;
			label: string;
		}[];
	};
	graphLimit: {
		maxVolatility: number;
		maxPerformance: number;
		minVolatility: number;
		minPerformance: number;
	};
	list: Array<PerformanceVolatilityContributionListItem>;
	opts: PerformanceContributionVolatilitySelectionProps;
};

type PerformanceVolatilityContributionData = Record<
	"contributionVolatility" | "performanceVolatility",
	InvestmentContributionResponse
>;

function buildMarkers(scaledLimit: {
	maxVolatility: number;
	maxPerformance: number;
	minVolatility: number;
	minPerformance: number;
}) {
	return {
		markersVolatility: getGraphMarkers2({
			min: scaledLimit.minVolatility,
			max: scaledLimit.maxVolatility,
			nOfMarkers: 5,
			suffix: "%",
		}),
		markersPerformance: getGraphMarkers2({
			min: scaledLimit.minPerformance,
			max: scaledLimit.maxPerformance,
			nOfMarkers: 5,
			suffix: "%",
		}),
	};
}

export function getPerformanceVolatilityContributionProps(
	data: PerformanceVolatilityContributionData,
	comparison: PerformanceContributionVolatilitySelectionProps,
	filterSignificant: boolean,
): GetPerformanceVolatilityContributionProps {
	const { contributionVolatility, performanceVolatility } = data;

	if (comparison.kind === "composition") {
		const vol = contributionVolatility.currentOnInstruments ?? [];
		const perf = performanceVolatility.currentOnInstruments ?? [];
		const byTickerName = new Map<string, { name?: string; volatilityValue?: number; performanceValue?: number }>();
		for (const el of vol) {
			if (!el.tickerName) continue;
			const r = byTickerName.get(el.tickerName) ?? {};
			r.volatilityValue = el.value;
			r.name = el.name ?? r.name;
			byTickerName.set(el.tickerName, r);
		}
		for (const el of perf) {
			if (!el.tickerName) continue;
			const r = byTickerName.get(el.tickerName) ?? {};
			r.performanceValue = el.value;
			r.name = el.name ?? r.name;
			byTickerName.set(el.tickerName, r);
		}
		const rows: FlatInstrumentRow[] = Array.from(byTickerName.entries()).map(([tickerName, r], index) => ({
			index,
			value: r.name ?? tickerName,
			computedData: {
				performanceValue: r.performanceValue ?? null,
				volatilityValue: r.volatilityValue ?? null,
			},
		}));
		const filtered = filterSignificant
			? rows.filter(
					(r) =>
						(r.computedData.volatilityValue != null && Math.abs(r.computedData.volatilityValue) >= 0.01) ||
						(r.computedData.performanceValue != null && Math.abs(r.computedData.performanceValue) >= 0.01),
				)
			: rows;
		const limit = filtered.reduce(
			(acc, r) => ({
				maxVolatility: Math.max(acc.maxVolatility, r.computedData.volatilityValue ?? 0),
				maxPerformance: Math.max(acc.maxPerformance, r.computedData.performanceValue ?? 0),
				minVolatility: Math.min(acc.minVolatility, r.computedData.volatilityValue ?? 0),
				minPerformance: Math.min(acc.minPerformance, r.computedData.performanceValue ?? 0),
			}),
			{ maxVolatility: 0, maxPerformance: 0, minVolatility: 0, minPerformance: 0 },
		);
		const graphLimit = {
			maxVolatility: roundCustomByStep(limit.maxVolatility, 0.5),
			maxPerformance: roundCustomByStep(limit.maxPerformance, 0.5),
			minVolatility: roundCustomByStep(limit.minVolatility, 0.5),
			minPerformance: roundCustomByStep(limit.minPerformance, 0.5),
		};
		return {
			list: filtered.map((row) => ({ kind: "composition" as const, row })),
			graphColumnMarkers: buildMarkers(graphLimit),
			graphLimit,
			opts: comparison,
		};
	}

	const { selector, vsSelector } = comparison;

	const contribution = {
		currentVolatility: contributionVolatility.current,
		currentPerformance: performanceVolatility.current,
	};

	const aggregateData = {
		volatility: (contribution.currentVolatility ?? []).map((el) => ({
			assetClass: el.assetClass,
			geography: el.geography,
			microAssetClass: el.microAssetClass,
			originalVolatility: el,
			volatilityValue: el.value,
			id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
		})),

		performance: (contribution.currentPerformance ?? []).map((el) => ({
			assetClass: el.assetClass,
			geography: el.geography,
			microAssetClass: el.microAssetClass,
			originalPerformance: el,
			performanceValue: el.value,
			id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
		})),
	};

	const decupledByID = [
		...new window.Set([
			...aggregateData.performance.map((el) => el.id),
			...aggregateData.volatility.map((el) => el.id),
		]),
	];

	const result = decupledByID.map((id) => {
		const currentVTK = aggregateData.volatility.find((el) => el.id === id);
		const currentPTK = aggregateData.performance.find((el) => el.id === id);

		return {
			...currentVTK,
			...currentPTK,
		};
	});

	const computedContribution = (() => {
		const drilldown = DrillDownByValueMap(
			result ?? [],
			selector,
			vsSelector,
			{
				volatilityValue: (val) => objSumRounded(val, "volatilityValue"),
				performanceValue: (val) => objSumRounded(val, "performanceValue"),
				maxVol: (val) => objSumRounded(val, "volatilityValue"),
				minVol: (val) => objSumRounded(val, "volatilityValue"),
				maxPer: (val) => objSumRounded(val, "performanceValue"),
				minPer: (val) => objSumRounded(val, "performanceValue"),
			},
			builtInSortFnFor("volatilityValue"),
		);
		if (!filterSignificant) {
			return drilldown;
		}
		return {
			...drilldown,
			drilldownData: drilldown.drilldownData
				.filter(
					(x) =>
						(x.computedData.volatilityValue != null && Math.abs(x.computedData.volatilityValue) >= 0.01) ||
						(x.computedData.performanceValue != null && Math.abs(x.computedData.performanceValue) >= 0.01),
				)
				.map((x) => ({
					...x,
					drilldown: x.drilldown.filter(
						(y) =>
							(y.computedData.volatilityValue != null && Math.abs(y.computedData.volatilityValue) >= 0.01) ||
							(y.computedData.performanceValue != null && Math.abs(y.computedData.performanceValue) >= 0.01),
					),
				})),
		};
	})();

	const { drilldownData } = computedContribution;

	const list = drilldownData.flatMap(({ drilldown, ...props }) =>
		drilldown.map((item) => ({
			kind: "grouped" as const,
			drilldown: { ...item },
			group: { ...props },
		})),
	);

	const graphLimit = drilldownData.reduce(
		(acc, group) => {
			const rowValues = group.drilldown.reduce(
				(a, row) => {
					return {
						maxVolatility: Math.max(a.maxVolatility, row.computedData.maxVol ?? 0, group.computedData.maxVol ?? 0),
						maxPerformance: Math.max(a.maxPerformance, row.computedData.maxPer ?? 0, group.computedData.maxPer ?? 0),
						minVolatility: Math.min(a.minVolatility, row.computedData.minVol ?? 0, group.computedData.minVol ?? 0),
						minPerformance: Math.min(a.minPerformance, row.computedData.minPer ?? 0, group.computedData.minPer ?? 0),
					};
				},
				{ maxVolatility: 0, maxPerformance: 0, minVolatility: 0, minPerformance: 0 },
			);
			return {
				maxVolatility: Math.max(acc.maxVolatility, rowValues.maxVolatility),
				maxPerformance: Math.max(acc.maxPerformance, rowValues.maxPerformance),
				minVolatility: Math.min(acc.minVolatility, rowValues.minVolatility),
				minPerformance: Math.min(acc.minPerformance, rowValues.minPerformance),
			};
		},
		{ maxVolatility: 0, maxPerformance: 0, minVolatility: 0, minPerformance: 0 },
	);

	const scaledDrilldown = {
		maxVolatility: roundCustomByStep(graphLimit.maxVolatility, 0.5),
		maxPerformance: roundCustomByStep(graphLimit.maxPerformance, 0.5),
		minVolatility: roundCustomByStep(graphLimit.minVolatility, 0.5),
		minPerformance: roundCustomByStep(graphLimit.minPerformance, 0.5),
	};

	return { list, graphColumnMarkers: buildMarkers(scaledDrilldown), opts: comparison, graphLimit };
}

const PERFORMANCE_COLOR = "#005C8B";
const VOLATILITY_COLOR = "#572C7F";

type MetricRow = {
	computedData: { performanceValue: number | null; volatilityValue: number | null };
};

function renderPercentCell(value: number | null | undefined, cellProps: any, opts: { color: string; bold: boolean }) {
	return value == null ? (
		"N/A"
	) : (
		<TinyTableDataCell {...cellProps}>
			<span className={`tabular-nums ${opts.bold ? "font-semibold" : ""}`} style={{ color: opts.color }}>
				{formatNumber(value, 2)}%
			</span>
		</TinyTableDataCell>
	);
}

function renderGraphCell(
	value: number | null | undefined,
	cellProps: any,
	opts: { color: string; scale: { max: number; min: number }; bold: boolean },
) {
	if (value == null) {
		return "N/A";
	}
	return (
		<div
			style={{ ...cellProps.style, minHeight: 31 }}
			className={overrideClassName(cellProps.className, `flex grow ${opts.bold ? "font-semibold" : ""}`)}
		>
			<BarGraphPCSvg
				className="w-full"
				options={{
					resize: true,
					marksLabels: false,
					markerStep: 0.5,
					scale: opts.scale,
				}}
				data={[{ value, color: opts.color }]}
			/>
		</div>
	);
}

function renderGraphHeader(markers: Array<{ label: string }>) {
	return (props: any) => (
		<div
			style={{ ...props.style, minHeight: 31 }}
			className={overrideClassName(props.className, "flex items-center justify-between grow")}
		>
			<ForEach collection={markers}>
				{(m) => (
					<Text
						as="div"
						type="Body/S/BOLD-UPPERCASE"
						style={{ fontSize: tinyTableHeadCellFontSize }}
						color={themeCSSVars.palette_N500}
					>
						{m.label}
					</Text>
				)}
			</ForEach>
		</div>
	);
}

export function PerformanceVolatilityContribution({
	comparison,
	graphLimit,
	graphColumnMarkers,
	list,
}: PrintableProps<
	{
		data: PerformanceVolatilityContributionData;
		comparison: PerformanceContributionVolatilitySelectionProps;
		graphLimit: GetPerformanceVolatilityContributionProps["graphLimit"];
		graphColumnMarkers: GetPerformanceVolatilityContributionProps["graphColumnMarkers"];
	},
	GetPerformanceVolatilityContributionProps["list"][number]
>): React.JSX.Element {
	const { t } = useReportTranslation();

	const performanceScale = useMemo(
		() => ({ max: graphLimit.maxPerformance, min: graphLimit.minPerformance }),
		[graphLimit.maxPerformance, graphLimit.minPerformance],
	);
	const volatilityScale = useMemo(
		() => ({ max: graphLimit.maxVolatility, min: graphLimit.minVolatility }),
		[graphLimit.maxVolatility, graphLimit.minVolatility],
	);

	const tableGroupRows = useMemo(() => {
		if (comparison.kind !== "grouped") return [];
		const groupedList = list.flatMap((it) => (it.kind === "grouped" ? [it] : []));
		const grouped = groupBy(groupedList, ({ group }) => `index-${group.index}`);
		return typedObjectValues(grouped).map((entries) => ({
			...entries![0].group,
			rows: entries?.map(({ drilldown }) => ({ ...drilldown })) ?? [],
		}));
	}, [comparison.kind, list]);

	const compositionRows = useMemo(() => {
		if (comparison.kind !== "composition") return [];
		return list.flatMap((it) => (it.kind === "composition" ? [it.row] : []));
	}, [comparison.kind, list]);

	const groupedColumns = useCallback<TableWithGroupsProps<(typeof tableGroupRows)[number]>["columns"]>(
		({ expandColumn }) => {
			if (comparison.kind !== "grouped") return [];
			return [
				expandColumn,
				{
					name: "label",
					header: (props) => (
						<TinyTableHeadCell {...props}>
							{t(`REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TABLE.${comparison.selector}`)}
						</TinyTableHeadCell>
					),
					groupContent: (groupedRow, props) => (
						<TinyTableDataCell {...props}>
							<strong>{groupedRow.value as any}</strong>
						</TinyTableDataCell>
					),
					content: (row, props) => <TinyTableDataCell {...props}>{row.value as any}</TinyTableDataCell>,
					orderable: false,
					minWidth: 140,
				},
				{
					name: "performance",
					align: "end",
					header: (props) => (
						<TinyTableHeadCell {...props}>
							{t("REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TABLE.PORTFOLIO_PERFORMANCE")}
						</TinyTableHeadCell>
					),
					groupContent: (groupedRow, props) =>
						renderPercentCell(groupedRow.computedData.performanceValue, props, {
							color: PERFORMANCE_COLOR,
							bold: true,
						}),
					content: (row, props) =>
						renderPercentCell(row.computedData.performanceValue, props, {
							color: PERFORMANCE_COLOR,
							bold: false,
						}),
					orderable: false,
					width: 96,
				},
				{
					name: "graph1",
					orderable: false,
					width: 120,
					header: renderGraphHeader(graphColumnMarkers.markersPerformance),
					groupContent: ({ computedData: { performanceValue } }, props) =>
						renderGraphCell(performanceValue, props, {
							color: PERFORMANCE_COLOR,
							scale: performanceScale,
							bold: true,
						}),
					content: ({ computedData: { performanceValue } }, props) =>
						renderGraphCell(performanceValue, props, {
							color: PERFORMANCE_COLOR,
							scale: performanceScale,
							bold: false,
						}),
				},
				{
					name: "volatility",
					align: "end",
					header: (props) => (
						<TinyTableHeadCell {...props}>
							{t("REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TABLE.PORTFOLIO_VOLATILITY")}
						</TinyTableHeadCell>
					),
					groupContent: (groupedRow, props) =>
						renderPercentCell(groupedRow.computedData.volatilityValue, props, {
							color: VOLATILITY_COLOR,
							bold: true,
						}),
					content: (row, props) =>
						renderPercentCell(row.computedData.volatilityValue, props, {
							color: VOLATILITY_COLOR,
							bold: false,
						}),
					orderable: false,
					width: 96,
				},
				{
					name: "graph2",
					width: 120,
					orderable: false,
					header: renderGraphHeader(graphColumnMarkers.markersVolatility),
					groupContent: ({ computedData: { volatilityValue } }, props) =>
						renderGraphCell(volatilityValue, props, {
							color: VOLATILITY_COLOR,
							scale: volatilityScale,
							bold: true,
						}),
					content: ({ computedData: { volatilityValue } }, props) =>
						renderGraphCell(volatilityValue, props, {
							color: VOLATILITY_COLOR,
							scale: volatilityScale,
							bold: false,
						}),
				},
			];
		},
		[
			comparison,
			graphColumnMarkers.markersPerformance,
			graphColumnMarkers.markersVolatility,
			performanceScale,
			volatilityScale,
			t,
		],
	);

	const compositionColumns = useMemo<BaseTableProps<MetricRow>["columns"]>(
		() => [
			{
				name: "label",
				header: (props) => (
					<TinyTableHeadCell {...props}>
						{t("REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TABLE.INSTRUMENT")}
					</TinyTableHeadCell>
				),
				content: (row: any, props) => <TinyTableDataCell {...props}>{row.value}</TinyTableDataCell>,
				orderable: false,
				minWidth: 140,
			},
			{
				name: "performance",
				align: "end",
				header: (props) => (
					<TinyTableHeadCell {...props}>
						{t("REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TABLE.PORTFOLIO_PERFORMANCE")}
					</TinyTableHeadCell>
				),
				content: (row, props) =>
					renderPercentCell(row.computedData.performanceValue, props, {
						color: PERFORMANCE_COLOR,
						bold: false,
					}),
				orderable: false,
				width: 96,
			},
			{
				name: "graph1",
				orderable: false,
				width: 120,
				header: renderGraphHeader(graphColumnMarkers.markersPerformance),
				content: ({ computedData: { performanceValue } }, props) =>
					renderGraphCell(performanceValue, props, {
						color: PERFORMANCE_COLOR,
						scale: performanceScale,
						bold: false,
					}),
			},
			{
				name: "volatility",
				align: "end",
				header: (props) => (
					<TinyTableHeadCell {...props}>
						{t("REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TABLE.PORTFOLIO_VOLATILITY")}
					</TinyTableHeadCell>
				),
				content: (row, props) =>
					renderPercentCell(row.computedData.volatilityValue, props, {
						color: VOLATILITY_COLOR,
						bold: false,
					}),
				orderable: false,
				width: 96,
			},
			{
				name: "graph2",
				width: 120,
				orderable: false,
				header: renderGraphHeader(graphColumnMarkers.markersVolatility),
				content: ({ computedData: { volatilityValue } }, props) =>
					renderGraphCell(volatilityValue, props, {
						color: VOLATILITY_COLOR,
						scale: volatilityScale,
						bold: false,
					}),
			},
		],
		[graphColumnMarkers.markersPerformance, graphColumnMarkers.markersVolatility, performanceScale, volatilityScale, t],
	);

	if (list.length === 0) {
		return <></>;
	}

	const titleKey =
		comparison.kind === "composition"
			? "COMPOSITION"
			: groupedOptions.find((opt) => opt.selector === comparison.selector && opt.vsSelector === comparison.vsSelector)!
					.key;

	return (
		<Card
			title={`${t("REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.TITLE")} - ${t(
				`REPORT_BUILDER.PERFORMANCE_AND_VOLATILITY_CONTRIBUTION.OPTIONS.${titleKey}`,
			)}`}
		>
			{comparison.kind === "composition" ? (
				<BaseTable
					palette="uniform"
					rowKey={(r) => r.index}
					rows={compositionRows}
					className="pointer-events-none"
					columns={compositionColumns}
				/>
			) : (
				<BaseTableWithGroups
					palette="uniform"
					groupRowKey={(group) => `index-${group.index}`}
					expandGroupByKey={Immutable.Set(tableGroupRows.map((group) => `index-${group.index}`))}
					groupedRows={tableGroupRows}
					className="pointer-events-none [&_.transition-\[height\]]:transition-none! [&_.transition-\[transform\,opacity\]]:transition-none!"
					columns={groupedColumns}
				/>
			)}
		</Card>
	);
}

export default PerformanceVolatilityContribution;
